import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function Analytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    submitted: 0,
    averageRating: "—",
    thisMonth: 0,
  });

  const [topRated, setTopRated] = useState([]);
  const [lowRated, setLowRated] = useState([]);
  const [industryStats, setIndustryStats] = useState([]);
  const [ratingDist, setRatingDist] = useState([]);

  useEffect(() => {
    if (!user) return;

    const loadAnalytics = async () => {
      try {
        setLoading(true);

        const q = query(
          collection(db, "evaluations"),
          where("evaluatorId", "==", user.uid)
        );

        const snap = await getDocs(q);

        let total = 0;
        let drafts = 0;
        let submitted = 0;
        let ratingSum = 0;
        let ratingCount = 0;
        let thisMonth = 0;

        const ratingsMap = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        const latestByFounder = {};

        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();

        snap.docs.forEach((d) => {
          const data = d.data();
          total++;

          const status = data.status || "draft";
          status === "submitted" ? submitted++ : drafts++;

          if (typeof data.rating === "number") {
            ratingSum += data.rating;
            ratingCount++;
            ratingsMap[data.rating]++;
          }

          if (data.createdAt?.toDate) {
            const created = data.createdAt.toDate();
            if (
              created.getMonth() === month &&
              created.getFullYear() === year
            ) {
              thisMonth++;
            }
          }

          const founderId = data.founderId;
          const updatedAt = data.updatedAt?.toMillis?.() || 0;

          if (
            !latestByFounder[founderId] ||
            updatedAt > latestByFounder[founderId].updatedAt
          ) {
            latestByFounder[founderId] = {
              founderId,
              rating: data.rating,
              status,
              updatedAt,
            };
          }
        });

        const founderIds = Object.keys(latestByFounder);

        const founderSnaps = await Promise.all(
          founderIds.map((id) => getDoc(doc(db, "users", id)))
        );

        const founderMap = {};
        founderSnaps.forEach((snap) => {
          if (snap.exists()) founderMap[snap.id] = snap.data();
        });

        const ranked = [];
        const industryMap = {};

        Object.values(latestByFounder).forEach((r) => {
          if (typeof r.rating !== "number") return;

          const founder = founderMap[r.founderId];
          if (!founder) return;

          const enriched = {
            ...r,
            startupName: founder.startupName || "Untitled Startup",
            industries: founder.industries || [],
          };

          ranked.push(enriched);

          enriched.industries.forEach((ind) => {
            if (!industryMap[ind]) {
              industryMap[ind] = { industry: ind, count: 0, ratingSum: 0 };
            }
            industryMap[ind].count++;
            industryMap[ind].ratingSum += enriched.rating;
          });
        });

        setTopRated([...ranked].sort((a, b) => b.rating - a.rating).slice(0, 3));
        setLowRated([...ranked].sort((a, b) => a.rating - b.rating).slice(0, 3));

        setIndustryStats(
          Object.values(industryMap)
            .map((i) => ({
              industry: i.industry,
              count: i.count,
              avg: +(i.ratingSum / i.count).toFixed(2),
            }))
            .sort((a, b) => b.avg - a.avg)
        );

        setRatingDist(
          Object.entries(ratingsMap).map(([k, v]) => ({
            rating: `⭐ ${k}`,
            count: v,
          }))
        );

        setStats({
          total,
          drafts,
          submitted,
          averageRating:
            ratingCount > 0 ? (ratingSum / ratingCount).toFixed(2) : "—",
          thisMonth,
        });
      } catch (err) {
        console.error("Failed to load analytics", err);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [user]);

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
        Loading analytics…
      </div>
    );

  return (
    <div>
      <h1 style={{ marginBottom: 6 }}>
        Analytics
      </h1>
      <p style={{ color: "#64748b", marginBottom: 28 }}>
        Deep insights from your startup evaluations
      </p>

      <Grid>
        <Stat label="Total Reviews" value={stats.total} />
        <Stat label="Drafts" value={stats.drafts} />
        <Stat label="Submitted" value={stats.submitted} />
        <Stat label="Average Rating" value={stats.averageRating} />
        <Stat label="This Month" value={stats.thisMonth} />
      </Grid>

      <Section title="⭐ Highest Rated Startups">
        {topRated.length === 0 ? (
          <Empty />
        ) : (
          topRated.map((s, i) => (
            <Rank
              key={s.founderId}
              rank={i + 1}
              startup={s.startupName}
              rating={s.rating}
              highlight
            />
          ))
        )}
      </Section>

      <Section title="⚠️ Lowest Rated Startups">
        {lowRated.length === 0 ? (
          <Empty />
        ) : (
          lowRated.map((s, i) => (
            <Rank
              key={s.founderId}
              rank={i + 1}
              startup={s.startupName}
              rating={s.rating}
            />
          ))
        )}
      </Section>

      <Section title="📊 Ratings Distribution">
        <Chart height={240}>
          <BarChart data={ratingDist}>
            <XAxis dataKey="rating" stroke="#64748b" />
            <YAxis allowDecimals={false} stroke="#64748b" />
            <Tooltip />
            <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
          </BarChart>
        </Chart>
      </Section>

      <Section title="🏭 Industry Average Ratings">
        <Chart height={280}>
          <BarChart data={industryStats}>
            <XAxis dataKey="industry" stroke="#64748b" />
            <YAxis domain={[0, 5]} stroke="#64748b" />
            <Tooltip />
            <Bar dataKey="avg" fill="#16a34a" radius={[6, 6, 0, 0]} />
          </BarChart>
        </Chart>
      </Section>

      <Section title="📝 Review Status">
        <Chart height={220}>
          <BarChart
            data={[
              { status: "Draft", value: stats.drafts },
              { status: "Submitted", value: stats.submitted },
            ]}
          >
            <XAxis dataKey="status" stroke="#64748b" />
            <YAxis allowDecimals={false} stroke="#64748b" />
            <Tooltip />
            <Bar dataKey="value" fill="#f59e0b" radius={[6, 6, 0, 0]} />
          </BarChart>
        </Chart>
      </Section>
    </div>
  );
}

/* ---------- UI COMPONENTS ---------- */

const Grid = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 18,
      marginBottom: 36,
    }}
  >
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div
    className="card"
    style={{
      padding: 22,
      borderRadius: 16,
      background: "linear-gradient(145deg,#ffffff,#f1f5f9)",
      boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
    }}
  >
    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontSize: 30, fontWeight: 700 }}>
      {value}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 36 }}>
    <h3 style={{ marginBottom: 14 }}>{title}</h3>
    {children}
  </div>
);

const Rank = ({ rank, startup, rating, highlight }) => (
  <div
    className="card"
    style={{
      padding: 18,
      marginBottom: 12,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderRadius: 14,
      background: highlight
        ? "linear-gradient(135deg,#fef3c7,#fde68a)"
        : "#ffffff",
      border: highlight ? "1px solid #facc15" : "1px solid #e5e7eb",
      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    }}
  >
    <strong>
      #{rank} {startup}
    </strong>
    <span style={{ fontWeight: 600 }}>⭐ {rating}/5</span>
  </div>
);

const Empty = ({ text = "No data yet." }) => (
  <div
    className="card"
    style={{
      padding: 24,
      color: "#64748b",
      background: "#f1f5f9",
      borderRadius: 14,
      textAlign: "center",
    }}
  >
    {text}
  </div>
);

const Chart = ({ children, height }) => (
  <div
    className="card"
    style={{
      padding: 18,
      height,
      minHeight: height,   // ✅ FIX: guarantees measurable height
      borderRadius: 16,
      background: "#ffffff",
      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    }}
  >
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </div>
);

export default Analytics;
